import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useTaskForm,
  type TaskApiPayload,
  type RecurrencePayload,
} from '@/features/tasks/model/forms/useTaskForm';
import { useAuthStore } from '@/store/auth';
import { fileToBase64Content } from '@/shared/utils/fileToBase64Content';
import { textOf, intOf } from '@/shared/utils/nullable';
import { getTaskDisplayName } from '@/utils/displayUtils';
import { uidNumber } from '@/features/tasks/mobile-calendar/utils/uidNumber';
import type { FormChecklistValue } from '@/features/tasks/task-dialogs/fields/FormChecklistBlock';
import type { RecurrencePeriod } from '@/features/tasks/task-dialogs/fields/RecurrenceBlock';
import {
  fetchTemplateFile,
  type TaskTemplate,
} from '@/entities/task/api/templates';
import type { Project } from '@/entities/task/api/projects';
import type { User } from '@/lib/api/users';
import type { Task } from '@/shared/types/task';
import type { PersonItem, TaskOptionItem } from './types';
import {
  normalizeIds,
  parsePriorityToNumber,
  deadlineAfterToISO,
  datetimeLocalToUTCISOString,
  uniqStrings,
  projectToCrmBinding,
  clampPriority,
  toggleId,
} from './utils';

interface UseTaskCreateFormParams {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TaskApiPayload) => void | Promise<void>;
  initialData?: Record<string, unknown>;
  mode: 'create' | 'edit';
}

export function useTaskCreateForm({
  open,
  onClose,
  onSubmit,
  initialData,
  mode,
}: UseTaskCreateFormParams) {
  const {
    submit,
    setValue,
    watch,
    errors,
    allUsers,
    allTasks,
    accomplices,
    auditors,
    responsibleId,
    showGroupDropdown,
  } = useTaskForm({
    open,
    initialData,
    mode,
    onSubmit,
    onClose,
  });

  /* ─── Recurrence state ─── */
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrencePeriod, setRecurrencePeriod] =
    useState<RecurrencePeriod>('day');
  const [recurrenceFirstAt, setRecurrenceFirstAt] = useState('');
  const [recurrenceEvery, setRecurrenceEvery] = useState(1);
  const [recurrenceUntilAt, setRecurrenceUntilAt] = useState('');

  /* ─── Users ─── */
  const users = allUsers ?? [];
  const user = useAuthStore((s) => s.userId);

  const assignees = useMemo(() => {
    return users
      .filter((u: User) => String(u.Active ?? 'Y') === 'Y')
      .map((u: User) => {
        const id = u.ID;
        const first = (u.Name?.String ?? '').trim();
        const last = (u.LastName?.String ?? '').trim();
        const name = `${first} ${last}`.trim() || `User #${id}`;
        const avatarUrl: string | null = u.photo ?? null;
        return { id, name, avatarUrl };
      })
      .filter((x) => Number.isFinite(x.id) && x.id > 0);
  }, [users]);

  const people: PersonItem[] = useMemo(() => {
    return assignees.map((a) => ({
      id: Number(a.id),
      name: a.name,
      avatarUrl: a.avatarUrl ?? null,
    }));
  }, [assignees]);

  const nameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of people) {
      m.set(p.id, p.name);
    }
    return m;
  }, [people]);

  /* ─── Creator ─── */
  const [creatorLocalId, setCreatorLocalId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (user && Number.isFinite(user) && user > 0) {
      setValue('CREATED_BY', user, { shouldValidate: false });
    }
  }, [open, user, setValue]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const finalCreator = creatorLocalId ?? user;
    if (finalCreator && Number.isFinite(finalCreator) && finalCreator > 0) {
      setValue('CREATED_BY', finalCreator, { shouldValidate: false });
    }
  }, [open, creatorLocalId, user, setValue]);

  /* ─── Prefill from initialData (once per open) ─── */
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!open) {
      prefilledRef.current = false;
      return;
    }
    if (prefilledRef.current || !initialData) {
      return;
    }
    prefilledRef.current = true;

    const title = String(initialData.TITLE ?? '');
    if (title) {
      setValue('TITLE', title, { shouldValidate: true });
    }
    const description = String(initialData.DESCRIPTION ?? '');
    if (description) {
      setValue('DESCRIPTION', description, { shouldValidate: false });
    }
    const responsible = Number(initialData.RESPONSIBLE_ID ?? 0);
    if (Number.isFinite(responsible) && responsible > 0) {
      setValue('RESPONSIBLE_ID', responsible, { shouldValidate: true });
    }
    const parent = Number(initialData.PARENT_ID ?? 0);
    if (Number.isFinite(parent) && parent > 0) {
      setValue('PARENT_ID', parent, { shouldValidate: false });
    }
    const group = Number(initialData.GROUP_ID ?? 0);
    if (Number.isFinite(group) && group > 0) {
      setValue('GROUP_ID', group, { shouldValidate: false });
    }
    const deadline = String(initialData.DEADLINE ?? '');
    if (deadline) {
      setValue('DEADLINE', deadline, { shouldValidate: true });
    }
    const planHours = Number(initialData.TIME_ESTIMATE_HOURS ?? 0);
    if (Number.isFinite(planHours) && planHours > 0) {
      setValue('TIME_ESTIMATE_HOURS', planHours, { shouldValidate: false });
    }
    const accomplicesPrefill = normalizeIds(initialData.ACCOMPLICES);
    if (accomplicesPrefill.length > 0) {
      setValue('ACCOMPLICES', accomplicesPrefill, { shouldValidate: false });
    }
    const auditorsPrefill = normalizeIds(initialData.AUDITORS);
    if (auditorsPrefill.length > 0) {
      setValue('AUDITORS', auditorsPrefill, { shouldValidate: false });
    }
    const crmPrefill = Array.isArray(initialData.UF_CRM_TASK)
      ? uniqStrings((initialData.UF_CRM_TASK as unknown[]).map(String))
      : [];
    if (crmPrefill.length > 0) {
      setValue('UF_CRM_TASK', crmPrefill, { shouldValidate: false });
    }
  }, [open, initialData, setValue]);

  /* ─── Accomplices / Auditors ─── */
  const accompliceIds: number[] = useMemo(() => {
    const arr = Array.isArray(accomplices) ? accomplices : [];
    return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }, [accomplices]);

  const auditorIds: number[] = useMemo(() => {
    const arr = Array.isArray(auditors) ? auditors : [];
    return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }, [auditors]);

  const setAccomplicesIds = useCallback(
    (next: number[]) => {
      setValue('ACCOMPLICES', next, { shouldValidate: true });
    },
    [setValue]
  );

  const setAuditorsIds = useCallback(
    (next: number[]) => {
      setValue('AUDITORS', next, { shouldValidate: true });
    },
    [setValue]
  );

  /* ─── Files ─── */
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  /** Длительность записанных голосовых по имени файла, мс. Уходит в payload
   *  (`durationMs`), иначе в созданной задаче плеер показывал бы «0:00». */
  const [voiceDurations, setVoiceDurations] = useState<Record<string, number>>(
    {}
  );

  /* ─── Checklist ─── */
  const [formChecklist, setFormChecklist] = useState<FormChecklistValue>(
    () => ({
      checklists: [],
      items: [],
    })
  );

  /* ─── Reset on close ─── */
  useEffect(() => {
    if (open) {
      return;
    }
    setFormChecklist({ checklists: [], items: [] });
    setAttachedFiles([]);
    setCreatorLocalId(null);
    setIsRecurring(false);
    setRecurrenceFirstAt('');
    setRecurrenceUntilAt('');
    setRecurrenceEvery(1);
    setRecurrencePeriod('day');
  }, [open]);

  /* ─── Sync files → form ─── */
  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;

    (async () => {
      if (!attachedFiles.length) {
        setValue('files', [], { shouldValidate: false });
        return;
      }

      const apiFiles = await Promise.all(
        attachedFiles.map(async (f) => ({
          name: f.name,
          content: await fileToBase64Content(f),
          durationMs: voiceDurations[f.name] || undefined,
        }))
      );

      if (cancelled) {
        return;
      }
      setValue('files', apiFiles, { shouldValidate: false });
    })();

    return () => {
      cancelled = true;
    };
  }, [open, attachedFiles, voiceDurations, setValue]);

  /* ─── Sync checklist → form ─── */
  useEffect(() => {
    setValue('CHECKLIST', formChecklist as FormChecklistValue, {
      shouldValidate: false,
      shouldDirty: true,
    });
  }, [formChecklist, setValue]);

  /* ─── Recurrence sync ─── */
  useEffect(() => {
    if (!open) {
      return;
    }

    if (!isRecurring) {
      setValue('recurrence', undefined, { shouldValidate: false });
      return;
    }

    const frequency: RecurrencePayload['frequency'] =
      recurrencePeriod === 'day'
        ? 'DAILY'
        : recurrencePeriod === 'week'
          ? 'WEEKLY'
          : 'MONTHLY';

    const interval = Number.isFinite(recurrenceEvery)
      ? Math.max(1, Math.min(365, recurrenceEvery))
      : 1;

    const startISO = datetimeLocalToUTCISOString(
      recurrenceFirstAt?.trim() ?? ''
    );

    const endISO = recurrenceUntilAt?.trim()
      ? datetimeLocalToUTCISOString(recurrenceUntilAt.trim())
      : undefined;

    const responsible = Number(watch('RESPONSIBLE_ID') ?? 0) || undefined;
    const creator = Number(watch('CREATED_BY') ?? 0) || undefined;

    const payload: RecurrencePayload = {
      frequency,
      interval,
      startDate: startISO,
      endDate: endISO,
      creatorId: creator,
      responsibleId: responsible,
    };

    setValue('recurrence', payload, { shouldValidate: true });
  }, [
    open,
    isRecurring,
    recurrencePeriod,
    recurrenceEvery,
    recurrenceFirstAt,
    recurrenceUntilAt,
    setValue,
    watch,
  ]);

  /* ─── Execution time ─── */
  const timeEstimateH = watch('TIME_ESTIMATE_HOURS');
  const timeEstimateM = watch('TIME_ESTIMATE_MINUTES');

  const executionTimeHHMM = useMemo(() => {
    const hh = Number(timeEstimateH ?? 0);
    const mm = Number(timeEstimateM ?? 0);
    const H = Number.isFinite(hh) ? Math.max(0, Math.min(999, hh)) : 0;
    const M = Number.isFinite(mm) ? Math.max(0, Math.min(59, mm)) : 0;
    return `${String(H).padStart(2, '0')}:${String(M).padStart(2, '0')}`;
  }, [timeEstimateH, timeEstimateM]);

  const setExecutionTime = useCallback(
    (v: string) => {
      const raw = v.trim();
      const parts = raw.split(':').map((p) => p.replace(/[^\d]/g, ''));
      const hh = Math.max(0, Math.min(999, parseInt(parts[0] || '0', 10) || 0));
      const mm = Math.max(0, Math.min(59, parseInt(parts[1] || '0', 10) || 0));
      setValue('TIME_ESTIMATE_HOURS', hh, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('TIME_ESTIMATE_MINUTES', mm, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setValue]
  );

  /* ─── Derived form values ─── */
  const deadline = watch('DEADLINE') ?? '';
  // Срок пришёл снаружи (клик по слоту календаря) — поле не должно
  // самозаполняться текущим временем поверх него.
  const hasInitialDeadline = Boolean(initialData?.DEADLINE);
  const parentId = watch('PARENT_ID') ?? null;
  const title = watch('TITLE') ?? '';
  const description = watch('DESCRIPTION') ?? '';

  const currentTaskId = Number(initialData?.ID ?? initialData?.id ?? 0) || 0;

  const parentSelectedId = useMemo(() => {
    const n = Number(parentId ?? 0);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [parentId]);

  const taskOptions = useMemo<TaskOptionItem[]>(() => {
    const list: Task[] = Array.isArray(allTasks) ? allTasks : [];
    return list
      .map((t) => {
        const id = Number(t.id ?? 0);
        if (!Number.isFinite(id) || id <= 0) {
          return null;
        }
        if (currentTaskId > 0 && id === currentTaskId) {
          return null;
        }
        const title = getTaskDisplayName(t).trim() || `Задача #${id}`;
        return { id, title } satisfies TaskOptionItem;
      })
      .filter(Boolean) as TaskOptionItem[];
  }, [allTasks, currentTaskId]);

  /* ─── Template application ─── */
  const applyTemplate = useCallback(
    (t: TaskTemplate) => {
      setValue('TITLE', textOf(t.TITLE ?? ''), { shouldValidate: true });
      setValue('DESCRIPTION', textOf(t.DESCRIPTION ?? ''), {
        shouldValidate: true,
      });
      setValue('PRIORITY', parsePriorityToNumber(t.PRIORITY), {
        shouldValidate: false,
      });

      const responsible = intOf(t.RESPONSIBLE_ID);
      if (responsible > 0) {
        setValue('RESPONSIBLE_ID', responsible, { shouldValidate: true });
      }

      const creator = intOf(t.CREATED_BY);
      if (creator > 0) {
        setCreatorLocalId(creator);
        setValue('CREATED_BY', creator, { shouldValidate: false });
      }

      setValue('ACCOMPLICES', normalizeIds(t.ACCOMPLICES), {
        shouldValidate: true,
      });
      setValue('AUDITORS', normalizeIds(t.AUDITORS), {
        shouldValidate: true,
      });

      if (Array.isArray(t.TAGS) && t.TAGS.length > 0) {
        setValue('TAGS', t.TAGS, { shouldValidate: false });
      }
      const groupId = intOf(t.GROUP_ID);
      if (groupId > 0) {
        setValue('GROUP_ID', groupId, { shouldValidate: false });
      }

      const dl = deadlineAfterToISO(intOf(t.DEADLINE_AFTER));
      if (dl) {
        setValue('DEADLINE', dl, { shouldValidate: true });
      }

      const checklistItems = Array.isArray(t.CHECKLIST_ITEMS)
        ? t.CHECKLIST_ITEMS
        : [];

      const checklistId = uidNumber();

      setFormChecklist({
        checklists: [
          {
            id: checklistId,
            title: textOf(t.TITLE) ? `Чеклист: ${textOf(t.TITLE)}` : 'Чеклист',
          },
        ],
        items: checklistItems
          .slice()
          .sort((a, b) => Number(a.SORT ?? 0) - Number(b.SORT ?? 0))
          .map((x) => ({
            id: Number(x.ID ?? uidNumber()),
            checklistId,
            title: textOf(x.TITLE),
            isComplete: Number(x.CHECKED ?? 0) === 1,
            parentId: null,
            members: [],
            files: [],
          })),
      });

      // Вложения шаблона: FILES — id файлов. Докачиваем их по прокси и кладём
      // в attachedFiles; эффект синхронизации сам переведёт их в base64 (files).
      const templateFileIds = normalizeIds(t.FILES);
      if (templateFileIds.length > 0) {
        void (async () => {
          const loaded = await Promise.all(
            templateFileIds.map((id) =>
              fetchTemplateFile(id).catch((e) => {
                console.error('template file fetch failed', id, e);
                return null;
              })
            )
          );
          const files = loaded.filter((f): f is File => f != null);
          if (files.length > 0) {
            setAttachedFiles((prev) => [...prev, ...files]);
          }
        })();
      }
    },
    [setValue]
  );

  /* ─── Smart-process CRM binding ─── */
  const applyCrmBinding = useCallback(
    (project: Project) => {
      const bind = projectToCrmBinding(project);
      if (bind) {
        const prev = watch('UF_CRM_TASK') ?? [];
        setValue('UF_CRM_TASK', uniqStrings([...prev, bind]), {
          shouldValidate: false,
        });
      }
    },
    [setValue, watch]
  );

  /** Привязать произвольный CRM-реф (напр. сделку "D_123" или лид "L_45"). */
  const applyCrmRef = useCallback(
    (ref: string) => {
      const bind = String(ref ?? '').trim();
      if (!bind) {
        return;
      }
      const prev = watch('UF_CRM_TASK') ?? [];
      setValue('UF_CRM_TASK', uniqStrings([...prev, bind]), {
        shouldValidate: false,
      });
    },
    [setValue, watch]
  );

  return {
    // form
    submit,
    setValue,
    watch,
    errors,
    showGroupDropdown,

    // users / people
    user,
    assignees,
    people,
    nameById,
    responsibleId,

    // creator
    creatorLocalId,
    setCreatorLocalId,

    // accomplices / auditors
    accompliceIds,
    auditorIds,
    setAccomplicesIds,
    setAuditorsIds,
    toggleId,

    // files
    fileInputRef,
    attachedFiles,
    setAttachedFiles,
    voiceDurations,
    setVoiceDurations,

    // checklist
    formChecklist,
    setFormChecklist,

    // recurrence
    isRecurring,
    setIsRecurring,
    recurrencePeriod,
    setRecurrencePeriod,
    recurrenceFirstAt,
    setRecurrenceFirstAt,
    recurrenceEvery,
    setRecurrenceEvery,
    recurrenceUntilAt,
    setRecurrenceUntilAt,

    // time / deadline
    executionTimeHHMM,
    setExecutionTime,
    deadline,
    hasInitialDeadline,
    title,
    description,
    parentSelectedId,
    taskOptions,
    currentTaskId,

    // actions
    applyTemplate,
    applyCrmBinding,
    applyCrmRef,
    clampPriority,
  };
}
